/**
 * WebSocket Channel 事件类型
 * value 用数字，可节省代码量。
 */
const ChannelEvent = {
  SETUP_SUCCESS: '1',
  CONNECTION_STATE_CHANGED: '2',
  CONNECTED: '3',
  /** WebSocket 重连失败 */
  RECONNECT_FAILED: '4',
  SETUP_FAILED: '5',
  /** WebSocket 连接状态变化通知 */
  DISCONNECTED: '6',
  CLOSE: '7',
  ONLINE: '8'
} as const;

/**
 * WebSocket 连接状态
 */
const enum SignalConnectionState {
  /** WebSocket 断开连接 */
  DISCONNECTED = 'DISCONNECTED',
  /** WebSocket 正在连接 */
  CONNECTING = 'CONNECTING',
  /** WebSocket 连接成功 */
  CONNECTED = 'CONNECTED'
}

/**
 * Signal 事件映射表
 */
const SignalEventMap = {
  /** 用户被踢出房间 (Relay超时、同名用户登录等) */
  CLIENT_BANNED: 0x09,
  /** Signal Channel 建立结果 */
  CHANNEL_SETUP_RESULT: 0x13,
  /** Signal Channel 重连结果 */
  CHANNEL_RECONNECT_RESULT: 0x202,
  /** 用户进房返回结果 */
  JOIN_ROOM_RESULT: 0x14,
  /** 远端用户进房 */
  PEER_JOIN: 0x1026,
  /** 远端用户退房 */
  PEER_LEAVE: 0x1027,
  /** 远端用户推流 */
  STREAM_ADDED: 0x10,
  /** 远端用户取消推流 */
  STREAM_REMOVED: 0x12,
  /** 上行网络质量数据 */
  UPLINK_NETWORK_STATS: 0x16,
  /** 远端用户音视频mute状态更新 */
  UPDATE_REMOTE_MUTE_STAT: 0x17,

  /** 推流回包 */
  PUBLISH_RESULT: 0x1002,
  /** 推流状态变更回包 */
  PUBLISH_STATE_CHANGE_RESULT: 0x1010,
  /** 取消回包 */
  UNPUBLISH_RESULT: 0x1004,
  /** 订阅回包，带有 answer sdp */
  SUBSCRIBE_RESULT: 0x1006,
  /** 取消订阅回包 */
  UNSUBSCRIBE_RESULT: 0x1008,
  /** 订阅状态变更回包 */
  SUBSCRIBE_CHANGE_RESULT: 0x100a,
  /** Mute 信令回包 */
  MUTE_RESULT: 0x100c,

  /** 上行 update offer 回包，带有 answer */
  UPDATE_OFFER_RESULT: 0x1020,

  /* on_start_publishing res */
  START_PUBLISH_TENCENT_CDN_RES: 0x506,
  /* on_stop_publishing res */
  STOP_PUBLISH_TENCENT_CDN_RES: 0x508,
  /* on_start_push_user_cdn res */
  START_PUBLISH_GIVEN_CDN_RES: 0x309,
  /* on_stop_push_user_cdn */
  STOP_PUBLISH_GIVEN_CDN_RES: 0x30b,
  /* start-mix-transcode res */
  START_MIX_TRANSCODE_RES: 0x30d,
  /* stop-mix-transcode res */
  STOP_MIX_TRANSCODE_RES: 0x30f,
  /* start-publish-cdn-stream res */
  START_PUBLISH_CDN_STREAM_RES: 0x2004,
  UPDATE_PUBLISH_CDN_STREAM_RES: 0x2006,
  STOP_PUBLISH_CDN_STREAM_RES: 0x2008,

  /* on_get_user_list res  */
  USER_LIST_RES: 0x1029,
  /* on_switch_role res */
  SWITCH_ROLE_RES: 0x100e,
  // 更新 constraint config result
  UPDATE_CONSTRAINT_CONFIG_RES: 0x304,
  REBUILD_PEER_CONNECTION_RES: 0x1036,

  SPC_PUBLISH_RESULT: 0x1032,
  SPC_SUBSCRIBE_RESULT: 0x103c,

  ABILITY_STATUS_REPORT_RESULT: 0x103e,
  SERVER_FIRST_PACKAGE_RECEIVED: 0x1388,

  RECEIVE_CUSTOM_MSG: 0x102c,
  FALLBACK_CODEC: 0x42,

  // switch-room
  SEND_SWITCH_ROOM_RES: 0x1040,   // 4160
  SEND_SWITCH_ROOM_SUBED_REQ: 0x1041, // 4161
  UPDATE_NETWORK_TIME_RESULT: 5001,
  CUSTOM_CMD_RES: 0x201c // 8820
} as const;

// 收到下列信令时，不输出日志
const SignalEventsForLogIgnored = [
  SignalEventMap.UPDATE_REMOTE_MUTE_STAT,
  SignalEventMap.UPLINK_NETWORK_STATS,
  SignalEventMap.USER_LIST_RES,
  SignalEventMap.MUTE_RESULT,
  SignalEventMap.SERVER_FIRST_PACKAGE_RECEIVED,
  SignalEventMap.RECEIVE_CUSTOM_MSG,
  SignalEventMap.UPDATE_NETWORK_TIME_RESULT
] as const;

/**
 * Signal 事件，与 SignalEventMap 一一对应
 */
const SignalEvent = {
  /** 用户被踢出房间 (Relay超时、同名用户登录等) */
  CLIENT_BANNED: 'client-banned',
  /** Signal Channel 建立成功 */
  CHANNEL_SETUP_RESULT: 'channel-setup-result',
  /** Signal Channel 重连结果 */
  CHANNEL_RECONNECT_RESULT: 'channel-reconnect-result',
  /** 用户进房返回结果 */
  JOIN_ROOM_RESULT: 'join-room-result',
  /** 远端用户进房 */
  PEER_JOIN: 'peer-join',
  /** 远端用户退房 */
  PEER_LEAVE: 'peer-leave',
  /** 远端用户推流 */
  STREAM_ADDED: 'stream-added',
  /** 远端用户取消推流 */
  STREAM_REMOVED: 'stream-removed',
  /** 上行网络质量数据 */
  UPLINK_NETWORK_STATS: 'uplink-network-stats',
  /** 远端用户音视频mute状态更新 */
  UPDATE_REMOTE_MUTE_STAT: 'update-remote-mute-stat',

  PUBLISH_RESULT: 'publish-result',
  PUBLISH_STATE_CHANGE_RESULT: 'publish-state-change-result',
  UNPUBLISH_RESULT: 'unpublish-result',
  SUBSCRIBE_RESULT: 'subscribe-result',
  SUBSCRIBE_CHANGE_RESULT: 'subscribe-change-result',
  UNSUBSCRIBE_RESULT: 'unsubscribe-result',
  // add/remove track 时，需要 update offer，该信令为 update offer result。
  // 带有后台发的 answer
  UPDATE_OFFER_RESULT: 'update-offer-result',
  /* on_start_publishing res */
  START_PUBLISH_TENCENT_CDN_RES: 'start-publish-tencent-cdn-res',
  /* on_stop_publishing res */
  STOP_PUBLISH_TENCENT_CDN_RES: 'stop-publish-tencent-cdn-res',
  /* start push cdn res */
  START_PUBLISH_GIVEN_CDN_RES: 'start-publish-given-cdn-res',
  /* stop push cdn res */
  STOP_PUBLISH_GIVEN_CDN_RES: 'stop-publish-given-cdn-res',
  /* start-mix-transcode res */
  START_MIX_TRANSCODE_RES: 'start-mix-transcode-res',
  /* stop-mix-transcode res */
  STOP_MIX_TRANSCODE_RES: 'stop-mix-transcode-res',
  /* stop-mix-transcode res */
  START_PUBLISH_CDN_STREAM_RES: 'start-publish-cdn-stream-res',
  UPDATE_PUBLISH_CDN_STREAM_RES: 'update-publish-cdn-stream-res',
  STOP_PUBLISH_CDN_STREAM_RES: 'stop-publish-cdn-stream-res',

  /* on_get_user_list res  */
  USER_LIST_RES: 'user-list-res',
  /* on_switch_role res */
  SWITCH_ROLE_RES: 'switch_role_res',
  MUTE_RESULT: 'mute-result',
  UPDATE_CONSTRAINT_CONFIG_RES: 'update-contraint-config-res',
  REBUILD_PEER_CONNECTION_RES: 'rebuild-pc-res',
  SPC_PUBLISH_RESULT: 'spc-publish-result',
  SPC_SUBSCRIBE_RESULT: 'spc-subscribe-result',
  ABILITY_STATUS_REPORT_RESULT: 'ability-status-report',
  SERVER_FIRST_PACKAGE_RECEIVED: 'first-pkg-received',
  RECEIVE_CUSTOM_MSG: 'receive-custom-msg',
  FALLBACK_CODEC: 'fallback-codec',
  /* switch-room */
  SEND_SWITCH_ROOM_RES: 'send-switch-room-res',
  SEND_SWITCH_ROOM_SUBED_REQ: 'send-switch-room-subed-res',
  UPDATE_NETWORK_TIME_RESULT: 'update_network_time_result',
  CUSTOM_CMD_RES: 'custom-cmd-res'
} as const;

/**
 * Signal 命令映射表
 * SignalCommand是SDK发往后台信令服务器的信令
 */
const SignalCommand = {
  // new signal command for updating sdp offer
  PUBLISH_CHANGE: 'publish_change',
  JOIN_ROOM: 'join',
  LEAVE_ROOM: 'leave',
  ON_QUALITY_REPORT: 'quality_report',
  UPDATE_MUTE_STAT: 'mute_uplink',

  PUBLISH: 'publish',
  PUBLISH_STATE_CHANGE: 'publish_state_change',
  UNPUBLISH: 'unpublish',

  SUBSCRIBE: 'subscribe',
  RECEIVE_DATA_USER_LIST: 'receive_data_userlist',
  UNSUBSCRIBE: 'unsubscribe',
  SUBSCRIBE_CHANGE: 'subscribe_change',

  START_PUBLISH_TENCENT_CDN: 'start_publishing',
  STOP_PUBLISH_TENCENT_CDN: 'stop_publishing',
  START_PUBLISH_GIVEN_CDN: 'start_push_user_cdn',
  STOP_PUBLISH_GIVEN_CDN: 'stop_push_user_cdn',
  START_MIX_TRANSCODE: 'start_mcu_mix',
  STOP_MIX_TRANSCODE: 'stop_mcu_mix',
  START_PUBLISH_CDN_STREAM: 'start_publish_cdn_stream', // https://iwiki.woa.com/p/1109917349#%E4%BA%8C%E5%8D%81%E5%9B%9B%E3%80%81%E6%96%B0%E6%B7%B7%E6%B5%81%E8%BD%AC%E6%8E%A8%E4%BF%A1%E4%BB%A4
  UPDATE_PUBLISH_CDN_STREAM: 'update_publish_cdn_stream',
  STOP_PUBLISH_CDN_STREAM: 'stop_publish_cdn_stream',

  GET_USER_LIST: 'get_user_list',
  SWITCH_ROLE: 'change_role',

  UPDATE_CONSTRAINT_CONFIG: 'update_constraint_config',

  REBUILD_PEER_CONNECTION: 'rebuild_pc',

  // 单 pc
  READY_TO_RECEIVE_DATA: 'ready_to_receive',
  SPC_JOIN_ROOM: 'join/v2',
  SPC_PUBLISH: 'publish/v2',
  SPC_SUBSCRIBE: 'subscribe/v3',

  // 能力位使用状态上报 https://tapd.woa.com/10155901/prong/stories/view/1010155901887078735
  ABILITY_STATUS_REPORT: 'ability_status_report',
  RECONNECT_WS: 'reconnect',
  SEND_CUSTOM_MSG: 'channel_msg',
  SWITCH_ROOM: 'switch_room',
  UPDATE_NETWORK_TIME: 'update_network_time',

  CUSTOM_CMD: 'abstract_cmd'
} as const;

const SendSignalToLog = new Set([
  SignalCommand.PUBLISH,
  SignalCommand.PUBLISH_CHANGE,
  SignalCommand.PUBLISH_STATE_CHANGE,
  SignalCommand.UNPUBLISH,
  SignalCommand.SUBSCRIBE,
  SignalCommand.SUBSCRIBE_CHANGE,
  SignalCommand.UNSUBSCRIBE,
  SignalCommand.SPC_PUBLISH,
  SignalCommand.SPC_SUBSCRIBE
] as const);

export {
  ChannelEvent,
  SignalConnectionState,
  SignalEventMap,
  SignalEvent,
  SignalCommand,
  SignalEventsForLogIgnored,
  SendSignalToLog
};
